import React, { useState, useEffect, useCallback } from "react";
import { usePollWhileLoading, NeverRunsWarning } from "@/components/rowSelection";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Clock, Calendar, Tag, Loader2, Pencil, Zap } from "lucide-react";
import { PromoMessageInput } from "@/components/PromoMessage";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOURS = Array.from({ length: 25 }, (_, i) => i);

const emptyPromo = () => ({
  name: "",
  target_type: "tags",
  tag_ids: [],
  tag_names: [],
  promo_codes: [],
  free_addons: false,
  message: "",
  product_ids: [],
  discount_type: "percentage",
  discount_value: 15,
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10),
  days_of_week: [0, 1, 2, 3, 4, 5, 6],
  hour_start: 11,
  hour_end: 14,
  enabled: true,
});

export default function PromotionsSchedule({ site, onChanged }) {
  const [promos, setPromos] = useState([]);
  const [tags, setTags] = useState([]);
  const [codes, setCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPromo());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, t] = await Promise.all([
        api.get(`/sites/${site.id}/promotions`),
        api.get(`/sites/${site.id}/promotions/status`),
        api.get(`/sites/${site.id}/tags`),
      ]);
      setPromos(p.data);
      setTags(t.data);
      const map = {};
      s.data.promotions.forEach((x) => (map[x.id] = x));
      setStatusMap(map);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [site.id]);

  useEffect(() => {
    load();
  }, [load]);

  const loadCodes = useCallback(async () => {
    try {
      const r = await api.get("/catalog", { params: { site_ids: site.id } });
      setCodes(r.data.all_promo_codes || []);
      setCodesLoading(r.data.loading_sites || []);
    } catch (e) {
      setCodesLoading([site.name]);
    }
  }, [site.id, site.name]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);
  usePollWhileLoading(codesLoading, loadCodes);

  const toggleCode = (c) =>
    setForm((f) => ({
      ...f,
      promo_codes: (f.promo_codes || []).includes(c) ? f.promo_codes.filter((x) => x !== c) : [...(f.promo_codes || []), c],
    }));

  const openNew = () => {
    setEditing(null);
    setForm(emptyPromo());
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...emptyPromo(), ...p });
    setOpen(true);
  };

  const toggleDay = (d) =>
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort(),
    }));

  const toggleTag = (t) =>
    setForm((f) => {
      const has = f.tag_ids.includes(t.id);
      return {
        ...f,
        tag_ids: has ? f.tag_ids.filter((x) => x !== t.id) : [...f.tag_ids, t.id],
        tag_names: has ? f.tag_names.filter((x) => x !== t.name) : [...f.tag_names, t.name],
      };
    });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (form.target_type === "tags" && form.tag_ids.length === 0 && !(form.promo_codes || []).length)
      return toast.error("Pick at least one tag or variation description code");
    setSaving(true);
    const payload = {
      ...form,
      discount_value: parseFloat(form.discount_value),
      hour_start: parseInt(form.hour_start),
      hour_end: parseInt(form.hour_end),
    };
    try {
      if (editing) await api.put(`/promotions/${editing.id}`, payload);
      else await api.post(`/sites/${site.id}/promotions`, payload);
      toast.success(editing ? "Promotion updated" : "Promotion created");
      setOpen(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/promotions/${id}`);
      toast.success("Promotion deleted");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleEnabled = async (id) => {
    try {
      await api.post(`/promotions/${id}/toggle`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-semibold">Scheduled Promotions</h3>
          <p className="text-sm text-muted-foreground">
            Sale prices turn on and off automatically by date, weekday and hour window.
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-promotion-button">
          <Plus className="w-4 h-4" /> New Promotion
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
        </p>
      ) : promos.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-16 text-center">
          <Zap className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No promotions yet. Create your first happy-hour deal.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {promos.map((p) => {
            const st = statusMap[p.id] || {};
            return (
              <div
                key={p.id}
                className="rounded-lg border border-border bg-card p-4 space-y-3"
                data-testid={`promotion-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-heading font-semibold">{p.name}</h4>
                      {st.active ? (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live
                        </Badge>
                      ) : st.in_window && p.enabled ? (
                        <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30">
                          Pending apply
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Scheduled</Badge>
                      )}
                    </div>
                    <p className="text-sm text-primary font-mono-num mt-1">
                      {p.discount_type === "percentage"
                        ? `-${p.discount_value}% off`
                        : p.discount_type === "set_price"
                        ? `→ $${p.discount_value}`
                        : `-$${p.discount_value} off`}
                      {p.free_addons && <span className="ml-2 text-xs" data-testid={`promo-free-addons-badge-${p.id}`}>+ free half &amp; half</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1" data-testid={`promo-store-${p.id}`}>
                      Store: <span className="text-foreground">{site.name}</span> only
                    </p>
                    {p.message && (
                      <p className="text-xs mt-1 italic" data-testid={`promo-message-${p.id}`}>Menu message: “{p.message}”</p>
                    )}
                    <NeverRunsWarning p={p} />
                  </div>
                  <Switch
                    checked={p.enabled}
                    onCheckedChange={() => toggleEnabled(p.id)}
                    data-testid={`toggle-promotion-${p.id}`}
                  />
                </div>

                <div className="space-y-1.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5" />
                    {p.target_type === "all" ? (
                      <span>All products</span>
                    ) : p.target_type === "tags" ? (
                      <span className="flex flex-wrap gap-1">
                        {p.tag_names.map((t) => (
                          <Badge key={t} variant="outline" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                        {(p.promo_codes || []).map((c) => (
                          <Badge key={`code-${c}`} variant="outline" className="text-[10px] font-mono-num border-primary/40 text-primary" data-testid={`promo-card-code-${p.id}-${c}`}>
                            {c}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      <span>{p.product_ids.length} products</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span className="font-mono-num">
                      {p.start_date} → {p.end_date}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="font-mono-num">
                      {String(p.hour_start).padStart(2, "0")}:00–{String(p.hour_end).padStart(2, "0")}:00
                    </span>
                    <span className="flex gap-0.5">
                      {DAYS.map((d, i) => (
                        <span
                          key={d}
                          className={`text-[10px] px-1 rounded ${
                            p.days_of_week.includes(i) ? "bg-primary/20 text-primary" : "text-muted-foreground/40"
                          }`}
                        >
                          {d[0]}
                        </span>
                      ))}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(p)}
                    data-testid={`edit-promotion-${p.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => remove(p.id)}
                    data-testid={`delete-promotion-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="promotion-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editing ? "Edit promotion" : "New promotion"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Promotion name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Lunch Happy Hour"
                data-testid="promo-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Applies to</Label>
                <Select
                  value={form.target_type}
                  onValueChange={(v) => setForm({ ...form, target_type: v })}
                >
                  <SelectTrigger data-testid="promo-target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tags">By tag and/or variation description code</SelectItem>
                    <SelectItem value="all">All products</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Discount type</Label>
                <Select
                  value={form.discount_type}
                  onValueChange={(v) => setForm({ ...form, discount_type: v })}
                >
                  <SelectTrigger data-testid="promo-discount-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage %</SelectItem>
                    <SelectItem value="set_price">Set price $</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <PromoMessageInput value={form.message || ""} onChange={(v) => setForm({ ...form, message: v })} testId="promo-message-input" />

            <label className="flex items-center gap-3 text-sm" data-testid="promo-free-addons">
              <Switch checked={!!form.free_addons} onCheckedChange={(v) => setForm({ ...form, free_addons: v })} data-testid="promo-free-addons-switch" />
              <span>
                Make half &amp; half upcharges free on promo sizes
                <span className="block text-xs text-muted-foreground">
                  Half &amp; half choices for the promo size are $0 while the promo is live, then restored. Extra toppings, crust and sauces keep their regular price.
                </span>
              </span>
            </label>

            {form.target_type === "tags" && (
              <div className="space-y-2">
                <Label>Product tags</Label>
                <div className="flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTag(t)}
                      className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${
                        form.tag_ids.includes(t.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                      data-testid={`promo-tag-${t.id}`}
                    >
                      {t.name} ({t.count})
                    </button>
                  ))}
                </div>
                <Label className="pt-2 block">By variation description code</Label>
                <div className="flex flex-wrap gap-2" data-testid="promo-code-chips">
                  {codes.length === 0 && (
                    <span className="text-xs text-muted-foreground">
                      {codesLoading.length ? "Loading codes from this store…" : "No codes on this store yet — add them in All Sites → Price by Size"}
                    </span>
                  )}
                  {codes.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCode(c)}
                      className={`px-2.5 py-1 rounded-md text-xs border font-mono-num transition-colors ${
                        (form.promo_codes || []).includes(c)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                      }`}
                      data-testid={`promo-code-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>{form.discount_type === "percentage" ? "Discount %" : "New sale price $"}</Label>
              <Input
                type="number"
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                className="font-mono-num"
                data-testid="promo-discount-value"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start date</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="promo-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End date</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  data-testid="promo-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Active days</Label>
              <div className="flex gap-1.5">
                {DAYS.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDay(i)}
                    className={`flex-1 py-2 rounded-md text-xs font-medium border transition-colors ${
                      form.days_of_week.includes(i)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                    data-testid={`promo-day-${i}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start hour</Label>
                <Select
                  value={String(form.hour_start)}
                  onValueChange={(v) => setForm({ ...form, hour_start: v })}
                >
                  <SelectTrigger data-testid="promo-hour-start">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.slice(0, 24).map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {String(h).padStart(2, "0")}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>End hour</Label>
                <Select
                  value={String(form.hour_end)}
                  onValueChange={(v) => setForm({ ...form, hour_end: v })}
                >
                  <SelectTrigger data-testid="promo-hour-end">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.slice(1).map((h) => (
                      <SelectItem key={h} value={String(h)}>
                        {String(h).padStart(2, "0")}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Times use the store timezone ({site.timezone}). The scheduler checks every 15 minutes.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-promotion-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {editing ? "Update" : "Create"} promotion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
